import toast from 'react-hot-toast'
import i18n from '../i18n'
import useAppStore from '../store/useAppStore'
import useCasesStore from '../store/useCasesStore'
import useMarketStore from '../store/useMarketStore'
import useInventoryStore from '../store/useInventoryStore'
import useTeamStore from '../features/team/store/useTeamStore'
import { isDevelopmentMode, generateMockInitData, DEFAULT_MOCK_USER, DEV_AUTH_HEADER } from '../utils/mockData'

const WEB_AUTH_TOKEN_COOKIE = 'skinoz_web_auth_token'
const WEB_AUTH_UID_COOKIE = 'skinoz_web_auth_uid'
const WEB_AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10
const AUTH_REF_COOKIE = 'skinoz_auth_ref'
const AUTH_REF_COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const GUEST_SHARD_COOKIE = 'skinoz_guest_shard'
const GUEST_SHARD_MAX_AGE = 60 * 60 * 24 * 365
const RUNTIME_ENVIRONMENT_STORAGE_KEY = 'skinoz_runtime_environment'
const GUEST_GET_PATHS = [
  /^\/api\/(?:cases|live|market|referral\/top|fast-event(?:\/weekly)?|event)\/$/,
  /^\/api\/event\/halloween\/(?:team\/[^/]+\/)?$/,
  /^\/api\/case\/[^/]+\/$/,
  /^\/api\/(?:double|wheel50)\/(?:\d+\/|history\/(?:fast\/)?|)?$/,
  /^\/api\/classic\/(?:\d+\/|history\/)?$/,
  /^\/api\/crash\/history\/(?:\d+\/)?$/,
  /^\/api\/mines\/$/,
  /^\/api\/casebattle\/(?:list\/|history\/(?:[^/]+\/)?)$/,
  /^\/api\/profile\/[^/]+\/(?:achievements\/)?$/,
  /^\/api\/team\/(?:|top\/|top\/history\/|capture\/|capture\/[^/]+\/|[^/]+\/(?:stats\/|games\/(?:[^/]+\/)?)?)$/
]

/**
 * API Service for SKINOZ Backend Integration
 * Handles unified API responses with multiple keys (cases, me, alert, error)
 * Implements load balancing across subdomains based on Telegram user ID
 */

class ApiService {
  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE || 'skinshub.ru'
    this.protocol = 'https'
    this.currentSubdomain = null
  }

  getCookie(name) {
    if (typeof document === 'undefined') {
      return null
    }

    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`))

    return match ? decodeURIComponent(match[1]) : null
  }

  setCookie(name, value, maxAge = WEB_AUTH_COOKIE_MAX_AGE) {
    if (typeof document === 'undefined') {
      return
    }

    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`
  }

  removeCookie(name) {
    if (typeof document === 'undefined') {
      return
    }

    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`
  }

  isValidWebAuthUid(uid) {
    return typeof uid === 'string' && /^\d+$/.test(uid)
  }

  getStoredWebAuth() {
    const token = this.getCookie(WEB_AUTH_TOKEN_COOKIE)
    const uid = this.getCookie(WEB_AUTH_UID_COOKIE)

    if (!token || !uid || !this.isValidWebAuthUid(uid)) {
      return null
    }

    return {
      token,
      uid,
      userId: Number(uid)
    }
  }

  saveWebAuth(token, uid) {
    this.setCookie(WEB_AUTH_TOKEN_COOKIE, token)
    this.setCookie(WEB_AUTH_UID_COOKIE, uid)
  }

  clearWebAuth() {
    this.removeCookie(WEB_AUTH_TOKEN_COOKIE)
    this.removeCookie(WEB_AUTH_UID_COOKIE)
    this.currentSubdomain = null
  }

  getStoredAuthRef() {
    return this.getCookie(AUTH_REF_COOKIE)
  }

  saveAuthRef(ref) {
    this.setCookie(AUTH_REF_COOKIE, ref, AUTH_REF_COOKIE_MAX_AGE)
  }

  clearStoredAuthRef() {
    this.removeCookie(AUTH_REF_COOKIE)
  }

  captureAuthRefFromUrl() {
    if (typeof window === 'undefined') {
      return null
    }

    const currentUrl = new URL(window.location.href)
    const ref = currentUrl.searchParams.get('ref')

    if (ref === null) {
      return this.getStoredAuthRef()
    }

    this.saveAuthRef(ref)
    console.log('✅ Auth ref сохранен из URL:', ref)

    return ref
  }

  getAuthBaseUrl() {
    return `https://auth.${this.baseUrl}`
  }

  getAuthLoginUrl(provider) {
    const authUrl = new URL(this.getAuthBaseUrl())
    const authRef = this.captureAuthRefFromUrl()

    authUrl.searchParams.set('action', 'login')
    authUrl.searchParams.set('provider', provider)

    if (authRef !== null) {
      authUrl.searchParams.set('ref', authRef)
    }

    return authUrl.toString()
  }

  getTelegramLaunchParams() {
    if (typeof window === 'undefined') {
      return {
        initData: null,
        platform: null,
        version: null
      }
    }

    const currentUrl = new URL(window.location.href)
    const hashValue = currentUrl.hash.startsWith('#')
      ? currentUrl.hash.slice(1)
      : currentUrl.hash
    const hashParams = new URLSearchParams(hashValue)
    const getLaunchParam = (name) => {
      return currentUrl.searchParams.get(name)
        || hashParams.get(name)
        || null
    }

    // Предпочитаем свойства из SDK (window.Telegram.WebApp) — они стабильны и
    // не пропадают при history.replaceState (например, после deep-link navigate).
    // URL-параметры (tgWebApp*) оставляем как fallback на случай, если SDK ещё не
    // успел проинициализироваться. Значение platform === 'unknown' означает,
    // что приложение открыто вне Telegram (обычный браузер), его игнорируем.
    const tg = window.Telegram?.WebApp
    const sdkPlatform = tg?.platform && tg.platform !== 'unknown' ? tg.platform : null
    const sdkVersion = tg?.version || null

    return {
      initData: tg?.initData || getLaunchParam('tgWebAppData'),
      platform: sdkPlatform || getLaunchParam('tgWebAppPlatform'),
      version: sdkVersion || getLaunchParam('tgWebAppVersion')
    }
  }

  getRuntimeEnvironment() {
    const telegramLaunchParams = this.getTelegramLaunchParams()
    const hasTelegramLaunchSignature = Boolean(
      telegramLaunchParams.initData
      && telegramLaunchParams.platform
      && telegramLaunchParams.version
    )

    if (typeof window === 'undefined') {
      return hasTelegramLaunchSignature ? 'telegram' : 'browser'
    }

    if (hasTelegramLaunchSignature) {
      window.sessionStorage.setItem(RUNTIME_ENVIRONMENT_STORAGE_KEY, 'telegram')
      return 'telegram'
    }

    const storedRuntimeEnvironment = window.sessionStorage.getItem(RUNTIME_ENVIRONMENT_STORAGE_KEY)
    return storedRuntimeEnvironment === 'telegram' && telegramLaunchParams.initData ? 'telegram' : 'browser'
  }

  hasTelegramInitData() {
    return Boolean(this.getTelegramLaunchParams().initData)
  }

  clearTelegramBrowserSession() {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.sessionStorage.removeItem('__telegram__initParams')
      window.sessionStorage.removeItem(RUNTIME_ENVIRONMENT_STORAGE_KEY)
    } catch (error) {
      console.warn('⚠️ Не удалось очистить sessionStorage Telegram init params:', error)
    }

    const currentUrl = new URL(window.location.href)
    const hashValue = currentUrl.hash.startsWith('#')
      ? currentUrl.hash.slice(1)
      : currentUrl.hash
    const hashParams = new URLSearchParams(hashValue)
    const telegramLaunchKeys = [
      'tgWebAppData',
      'tgWebAppVersion',
      'tgWebAppPlatform',
      'tgWebAppThemeParams',
      'tgWebAppStartParam',
      'tgWebAppBotInline',
      'tgWebAppShowSettings',
      'tgWebAppFullscreen',
      'tgWebAppDefaultColors'
    ]

    telegramLaunchKeys.forEach((key) => {
      currentUrl.searchParams.delete(key)
      hashParams.delete(key)
    })

    const nextHash = hashParams.toString()
    const nextUrl = `${currentUrl.pathname}${currentUrl.search}${nextHash ? `#${nextHash}` : ''}`
    window.history.replaceState({}, document.title, nextUrl)
  }

  clearBrowserAuthState() {
    this.clearWebAuth()

    if (this.getRuntimeEnvironment() !== 'telegram') {
      this.clearTelegramBrowserSession()
    }
  }

  cleanupAuthParamsFromUrl(redirectToRoot = false) {
    if (typeof window === 'undefined') {
      return
    }

    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.delete('token')
    currentUrl.searchParams.delete('uid')

    const targetUrl = redirectToRoot
      ? `${currentUrl.origin}/`
      : `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`

    window.history.replaceState({}, document.title, targetUrl)
  }

  initializeWebAuthFromUrl() {
    if (typeof window === 'undefined') {
      return { hasNewAuth: false, isInvalid: false }
    }

    this.captureAuthRefFromUrl()

    if (this.hasTelegramInitData()) {
      this.cleanupAuthParamsFromUrl(false)
      return { hasNewAuth: false, isInvalid: false }
    }

    const url = new URL(window.location.href)
    const token = url.searchParams.get('token')
    const uid = url.searchParams.get('uid')
    const hasAuthParams = token !== null || uid !== null

    if (!hasAuthParams) {
      return { hasNewAuth: false, isInvalid: false }
    }

    if (!token || !uid || !this.isValidWebAuthUid(uid)) {
      console.warn('⚠️ Получены некорректные параметры web-авторизации')
      this.clearWebAuth()
      this.cleanupAuthParamsFromUrl(true)
      return { hasNewAuth: false, isInvalid: true }
    }

    this.saveWebAuth(token, uid)
    this.clearStoredAuthRef()
    this.cleanupAuthParamsFromUrl(true)
    console.log('✅ Данные web-авторизации сохранены из URL')

    return {
      hasNewAuth: true,
      isInvalid: false,
      token,
      uid
    }
  }

  getAuthContext() {
    if (import.meta.env.MODE === 'development') {
      const token = window.localStorage.getItem('skinoz_dev_guest') === '1' ? 'guest' : DEV_AUTH_HEADER
      const isAuthorized = Boolean(token && token !== 'guest')
      return {
        isAuthorized,
        mode: isAuthorized ? 'development' : null,
        runtimeEnvironment: 'browser',
        token: isAuthorized ? token : null,
        userId: isAuthorized ? DEFAULT_MOCK_USER.id : null
      }
    }

    const telegramInitData = this.getTelegramInitData({ suppressWarnings: true })
    const runtimeEnvironment = this.getRuntimeEnvironment()

    if (telegramInitData) {
      return {
        isAuthorized: true,
        mode: 'telegram',
        runtimeEnvironment,
        token: telegramInitData,
        userId: window.Telegram?.WebApp?.initDataUnsafe?.user?.id || null
      }
    }

    const storedWebAuth = this.getStoredWebAuth()
    if (storedWebAuth) {
      return {
        isAuthorized: true,
        mode: 'web',
        runtimeEnvironment,
        token: storedWebAuth.token,
        userId: storedWebAuth.userId,
        uid: storedWebAuth.uid
      }
    }

    if (isDevelopmentMode() && window.localStorage.getItem('skinoz_dev_guest') !== '1') {
      this.mockInitData ||= generateMockInitData()
      return {
        isAuthorized: true,
        mode: 'development',
        runtimeEnvironment: 'browser',
        token: this.mockInitData,
        userId: DEFAULT_MOCK_USER.id
      }
    }

    return {
      isAuthorized: false,
      mode: null,
      runtimeEnvironment,
      token: null,
      userId: null
    }
  }

  /**
   * Get the appropriate subdomain based on Telegram user ID
   * Uses last digit of user ID for load balancing (0-9)
   */
  getSubdomain() {
    const authContext = this.getAuthContext()
    const { telegramUser } = useAppStore.getState()

    if (authContext.mode === 'web' && authContext.userId !== null) {
      const lastDigit = authContext.userId % 10
      this.currentSubdomain = `${lastDigit}.${this.baseUrl}`
      return this.currentSubdomain
    }

    if (telegramUser?.id) {
      const lastDigit = telegramUser.id % 10
      this.currentSubdomain = `${lastDigit}.${this.baseUrl}`
      return this.currentSubdomain
    }

    if (authContext.userId !== null) {
      const lastDigit = authContext.userId % 10
      this.currentSubdomain = `${lastDigit}.${this.baseUrl}`
      return this.currentSubdomain
    }

    if (authContext.runtimeEnvironment === 'browser') {
      let shard = this.getCookie(GUEST_SHARD_COOKIE)
      if (!/^[0-9]$/.test(shard || '')) {
        shard = String(Math.floor(Math.random() * 10))
        this.setCookie(GUEST_SHARD_COOKIE, shard, GUEST_SHARD_MAX_AGE)
      }
      this.currentSubdomain = `${shard}.${this.baseUrl}`
      return this.currentSubdomain
    }

    this.currentSubdomain = `0.${this.baseUrl}`
    return this.currentSubdomain
  }

  /**
   * Get full API URL with subdomain
   */
  getApiUrl(endpoint) {
    const subdomain = this.getSubdomain()
    return `${this.protocol}://${subdomain}${endpoint}`
  }

  /**
   * Initialize Telegram WebApp
   * Sets up WebApp configuration and validates environment
   */
  initializeTelegramWebApp() {
    if (isDevelopmentMode()) {
      console.log('🔧 Development mode: Skipping Telegram WebApp initialization')
      return true
    }
    
    if (!window.Telegram?.WebApp) {
      console.error('❌ Telegram WebApp не найден')
      return false
    }
    
    try {
      const tg = window.Telegram.WebApp
      
      // Проверяем версию WebApp
      if (!tg.isVersionAtLeast('6.0')) {
        console.warn('⚠️ Версия Telegram WebApp устарела, рекомендуется обновление')
      }
      
      // Настраиваем WebApp
      tg.ready()
      tg.expand()
      
      // Настраиваем тему
      tg.setHeaderColor('#0A0A0F')
      tg.setBackgroundColor('#0A0A0F')
      
      // Получаем данные пользователя
      const user = tg.initDataUnsafe?.user
      if (user) {
        const { setTelegramUser } = useAppStore.getState()
        setTelegramUser(user)
        console.log('✅ Telegram пользователь установлен:', user.first_name)
      }
      
      return true
    } catch (error) {
      console.error('❌ Ошибка инициализации Telegram WebApp:', error)
      return false
    }
  }

  /**
   * Get Telegram initData for authentication
   * Uses mock data for localhost development with validation
   */
  getTelegramInitData({ suppressWarnings = false } = {}) {
    // Check WebApp readiness
    if (!window.Telegram?.WebApp?.initData) {
      if (!suppressWarnings) {
        console.warn('⚠️ Telegram WebApp initData недоступен')
      }
      return null
    }
    
    const initData = window.Telegram.WebApp.initData
    
    // Validate initData
    if (initData.length < 10) {
      if (!suppressWarnings) {
        console.warn('⚠️ InitData кажется слишком коротким')
      }
      return null
    }
    
    // Check required fields
    const params = new URLSearchParams(initData)
    if (!params.get('user') || !params.get('auth_date')) {
      if (!suppressWarnings) {
        console.warn('⚠️ InitData не содержит обязательных полей')
      }
      return null
    }
    
    // Check auth_date freshness (not older than 24 hours)
    const authDate = parseInt(params.get('auth_date'))
    const now = Math.floor(Date.now() / 1000)
    if (now - authDate > 86400) {
      if (!suppressWarnings) {
        console.warn('⚠️ InitData устарел, рекомендуется перезапуск приложения')
      }
    }
    
    return initData
  }

  handleAuthorizationFailure(authContext, message = i18n.t('api.authExpired')) {
    console.error('❌ Обнаружена проблема авторизации')
    // Отзываем сессию чата поддержки (без ожидания: дальше редирект)
    import('./supportService').then(({ logoutSupport }) => logoutSupport())
    this.clearBrowserAuthState()

    const appState = useAppStore.getState()

    if (appState?.setAuthState) {
      appState.setAuthState({
        isAuthorized: false,
        authMode: null,
        runtimeEnvironment: this.getRuntimeEnvironment()
      })
    }

    if (appState?.setUser) {
      appState.setUser(null)
    }

    if (appState?.setTelegramUser) {
      appState.setTelegramUser(null)
    }

    if (appState?.setIsFirstVisit) {
      appState.setIsFirstVisit(false)
    }

    toast.error(message)

    if (authContext.runtimeEnvironment === 'telegram' && window.Telegram?.WebApp?.close) {
      setTimeout(() => {
        window.Telegram.WebApp.close()
      }, 2000)
    }

    if (authContext.runtimeEnvironment === 'browser' && typeof window !== 'undefined') {
      window.location.replace('/welcome')
    }

    const authError = new Error('Ошибка авторизации')
    authError.isHandledAuthError = true
    throw authError
  }

  transitionToGuest() {
    if (isDevelopmentMode()) window.localStorage.setItem('skinoz_dev_guest', '1')
    this.clearBrowserAuthState()
    useTeamStore.getState().reset()
    const state = useAppStore.getState()
    state.resetRushdrop()
    state.setAuthState({ isAuthorized: false, authMode: null, runtimeEnvironment: 'browser' })
    state.setUser(null)
    state.setTelegramUser(null)
    state.setBalance(0)
    state.setTasks([])
    state.setRecentWins([])
    state.dismissOnboardingPlusAward()
    state.setIsFirstVisit(false)
    useCasesStore.getState().invalidateLocalizedData()
    useCasesStore.setState({ currentCase: null, openingData: null, openingResult: null, openingMultipleResults: null })
    useMarketStore.getState().invalidate()
    useInventoryStore.setState({ items: [] })
    this.guestTransitionPromise = Promise.all([
      import('../features/crash/store/useCrashStore'),
      import('../features/double/store/useDoubleStore'),
      import('../features/wheel500/store/useWheel500Store'),
      import('../features/royale/store/useRoyaleStore'),
      import('../features/crash/services/crashSocket')
    ]).then(([{ default: crash }, { default: double }, { default: wheel }, { default: royale }, { default: socket }]) => {
      if (this.getAuthContext().isAuthorized) return
      crash.getState().reset()
      crash.setState({ inventory: [], balance: 0, myChatId: null })
      double.setState({ activeRound: null, inventory: [], balance: 0, ownBetAcks: [] })
      wheel.setState({ activeRound: null, inventory: [], balance: 0, ownBetAcks: [] })
      royale.setState({ activeRound: null, bets: [], users: [], teams: [], inventory: [], balance: 0, pendingBetIds: [], lastValidRound: null })
      socket.refreshAuthToken()
    })
  }

  isGuestAllowed(endpoint, method = 'GET') {
    if (method.toUpperCase() !== 'GET' || !endpoint.startsWith('/api/')) return false
    const rawPath = endpoint.split(/[?#]/, 1)[0]
    if (/%2f|%5c|%2e|\\|\/\.\.?\//i.test(rawPath)) return false
    const path = new URL(endpoint, 'https://guest.local').pathname
    if (path !== rawPath) return false
    let segments
    try {
      segments = path.split('/').filter(Boolean).map((segment) => decodeURIComponent(segment).toLowerCase())
    } catch {
      return false
    }
    if (segments[1] === 'team' && ['my', 'chat', 'applications', 'settings', 'treasury', 'audit', 'create', 'members', 'leave', 'transfer', 'announcement', 'colors', 'avatar', 'background', 'share', 'delete'].includes(segments[2])) return false
    if (segments[1] === 'profile' && ['me', 'achievements', 'visibility', 'colors', 'contacts', 'background', 'avatar', 'share'].includes(segments[2])) return false
    if (segments[1] === 'casebattle' && segments[2] === 'history' && segments[3] === 'me') return false
    return GUEST_GET_PATHS.some((pattern) => pattern.test(path))
  }

  async fetchWithAuth(endpoint, options = {}, retried = false) {
    const authContext = this.getAuthContext()
    const method = (options.method || 'GET').toUpperCase()
    if (!authContext.isAuthorized && (authContext.runtimeEnvironment !== 'browser' || !this.isGuestAllowed(endpoint, method))) {
      const error = new Error('Требуется вход')
      error.isHandledAuthError = true
      useAppStore.getState().openLogin?.()
      throw error
    }
    if (!authContext.isAuthorized && this.guestTransitionPromise) {
      await this.guestTransitionPromise
      if (this.getAuthContext().isAuthorized) {
        const error = new Error('Сессия изменилась')
        error.isHandledAuthError = true
        throw error
      }
    }

    const url = new URL(this.getApiUrl(endpoint))
    if (!authContext.isAuthorized && method === 'GET') {
      url.searchParams.set('lang', useAppStore.getState().language)
    }
    const response = await fetch(url, {
      ...options,
      method,
      headers: {
        ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        Accept: 'application/json',
        ...options.headers,
        'X-Telegram-Init-Data': authContext.token || 'guest'
      }
    })

    const currentContext = this.getAuthContext()
    if (currentContext.token !== authContext.token || currentContext.mode !== authContext.mode) {
      if (!retried && ['web', 'development'].includes(authContext.mode) && !currentContext.isAuthorized && this.isGuestAllowed(endpoint, method)) {
        return this.fetchWithAuth(endpoint, options, true)
      }
      const error = new Error('Сессия изменилась')
      error.isHandledAuthError = true
      throw error
    }

    if (response.status === 401) {
      if (authContext.runtimeEnvironment === 'browser' && ['web', 'development'].includes(authContext.mode)) {
        this.transitionToGuest()
        if (!retried && this.isGuestAllowed(endpoint, method)) return this.fetchWithAuth(endpoint, options, true)
        const error = new Error('Сессия истекла')
        error.isHandledAuthError = true
        throw error
      }
      if (!authContext.isAuthorized) {
        useAppStore.getState().openLogin?.()
        const error = new Error('Требуется вход')
        error.isHandledAuthError = true
        throw error
      }
      this.handleAuthorizationFailure(authContext)
    }
    return { response, authContext }
  }

  /**
   * Universal API request handler
   * Processes unified responses with multiple keys and enhanced error handling
   */
  async makeRequest(endpoint, options = {}, retried = false) {
    try {
      const languageRevision = useAppStore.getState().languageRevision
      const { skipBalanceSync, parseForbidden, ...fetchOptions } = options
      const { response, authContext } = await this.fetchWithAuth(endpoint, fetchOptions)
      
      if (response.status === 403 && !parseForbidden) {
        console.error('❌ Доступ запрещен (403)')
        const forbiddenError = new Error(i18n.t('api.accessDenied'))
        forbiddenError.isNotified = true
        toast.error(forbiddenError.message)
        throw forbiddenError
      }
      
      if (response.status === 429) {
        toast.error(i18n.t('api.rateLimited'))
        const error = new Error('rate_limited')
        error.isNotified = true
        throw error
      }

      if (!response.ok) {
        // Пытаемся парсить тело ответа для извлечения ошибки бэкенда
        try {
          const errorData = await response.json()
          if (errorData?.error) {
            // Ошибка бэкенда — пробрасываем без generic toast, вызывающий код обработает сам
            const backendError = new Error(errorData.error)
            backendError.isBackendError = true
            backendError.status = response.status
            backendError.code = errorData.code || errorData.error
            // Полное тело ошибки — для ответов с доп. данными (например, 409 price changed с новой котировкой)
            backendError.data = errorData
            throw backendError
          }
        } catch (parseErr) {
          // Если не JSON или нет поля error — пробрасываем generic-ошибку
          if (parseErr.isBackendError) {
            throw parseErr
          }
        }
        const httpError = new Error(`HTTP ${response.status}: ${response.statusText}`)
        httpError.status = response.status
        throw httpError
      }

      const data = await response.json()

      if (authContext.token !== this.getAuthContext().token || authContext.mode !== this.getAuthContext().mode) {
        if (!retried && ['web', 'development'].includes(authContext.mode) && !this.getAuthContext().isAuthorized && this.isGuestAllowed(endpoint, fetchOptions.method || 'GET')) {
          return this.makeRequest(endpoint, options, true)
        }
        const error = new Error('Сессия изменилась')
        error.isHandledAuthError = true
        throw error
      }

      if (data?.error_auth === true) {
        if (authContext.runtimeEnvironment === 'browser' && ['web', 'development'].includes(authContext.mode)) {
          this.transitionToGuest()
          if (!retried && this.isGuestAllowed(endpoint, fetchOptions.method || 'GET')) return this.makeRequest(endpoint, options, true)
          const error = new Error('Сессия истекла')
          error.isHandledAuthError = true
          throw error
        }
        if (!authContext.isAuthorized) {
          useAppStore.getState().openLogin()
          const error = new Error('Требуется вход')
          error.isHandledAuthError = true
          throw error
        }
        this.handleAuthorizationFailure(authContext)
      }

      // Process unified response
      if (languageRevision === useAppStore.getState().languageRevision &&
          authContext.mode === this.getAuthContext().mode) {
        this.processUnifiedResponse(data, {
          skipBalanceSync: typeof skipBalanceSync === 'function' ? skipBalanceSync() : skipBalanceSync,
          isProfileResponse: (fetchOptions.method || 'GET') === 'GET' && endpoint === '/api/me/'
        })
      }
      
      return data
    } catch (error) {
      if (error?.name === 'AbortError') {
        // Запрос отменен при размонтировании/смене страницы
        throw error
      }

      if (error?.isHandledAuthError || error?.isNotified) {
        throw error
      }

      console.error('❌ API Request failed:', error)

      // Ошибки бэкенда обрабатываются вызывающим кодом — не показываем generic toast
      if (error?.isBackendError) {
        throw error
      }

      // Enhanced error messaging
      if (error.isMissingAuth) {
        toast.error(i18n.t('api.authError'))
      } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        toast.error(i18n.t('api.networkError'))
      } else {
        toast.error(i18n.t('api.requestFailed'))
      }
      error.isNotified = true

      throw error
    }
  }

  /**
   * Process unified API response with multiple keys
   * Updates appropriate Zustand stores based on response keys
   */
  processUnifiedResponse(data, { skipBalanceSync = false, isProfileResponse = false } = {}) {
    // Handle errors FIRST - если есть ошибка, не обрабатываем остальные ключи
    // НЕ показываем toast здесь - это делается в userApi для правильной локализации
    if (data.error) {
      return // Прерываем обработку при ошибке, но не показываем toast
    }

    // Handle user data and balance
    if (data.me) {
      const state = useAppStore.getState()
      const { setUser, setBalance, setTasks, setIsFirstVisit } = state
      if (data.me.onboarding_plus?.status === 'awarded') {
        state.setOnboardingPlusAward(data.me.onboarding_plus)
      }
      
      // Обновляем пользователя только если данные изменились
      const currentUser = state.user
      const nextUser = { ...currentUser, ...data.me }
      if (isProfileResponse && !Object.prototype.hasOwnProperty.call(data.me, 'tjs_withdraw_enabled')) {
        delete nextUser.tjs_withdraw_enabled
      }
      // Ключ slot_enabled приходит только со значением true: пропал из профиля — режим скрыт
      if (isProfileResponse && !Object.prototype.hasOwnProperty.call(data.me, 'slot_enabled')) {
        delete nextUser.slot_enabled
      }
      // Ключ stars_withdraw_enabled: нет в профиле — вывод в Telegram Stars недоступен
      if (isProfileResponse && !Object.prototype.hasOwnProperty.call(data.me, 'stars_withdraw_enabled')) {
        delete nextUser.stars_withdraw_enabled
      }
      // Ключ rub_withdraw_enabled: нет в профиле — вывод в рублях (СБП / карта РФ) недоступен
      if (isProfileResponse && !Object.prototype.hasOwnProperty.call(data.me, 'rub_withdraw_enabled')) {
        delete nextUser.rub_withdraw_enabled
      }
      if (JSON.stringify(currentUser) !== JSON.stringify(nextUser)) {
        setUser(nextUser)
      }
      
      // Обновляем баланс только если изменился
      const rawBalance = data.me.balance !== undefined ? data.me.balance : data.me.points
      const normalizedBalance = Number(rawBalance)
      const nextBalance = Number.isFinite(normalizedBalance) ? normalizedBalance : 0

      if (!skipBalanceSync && rawBalance !== undefined && nextBalance !== Number(state.balance || 0)) {
        setBalance(nextBalance)
      }
      
      // Обновляем задания только если изменились
      if (data.me.tasks && JSON.stringify(data.me.tasks) !== JSON.stringify(state.tasks)) {
        setTasks(data.me.tasks)
      }
      
      // Обновляем статус первого визита для онбординга только если значение изменилось
      if (data.me.is_first_visit !== undefined) {
        const currentIsFirstVisit = state.isFirstVisit
        if (currentIsFirstVisit !== data.me.is_first_visit) {
          console.log('🎯 API получил новое is_first_visit:', data.me.is_first_visit, 'было:', currentIsFirstVisit)
          setIsFirstVisit(data.me.is_first_visit)
          console.log('✅ setIsFirstVisit вызван с значением:', data.me.is_first_visit)
        } else {
          console.log('⏸️ is_first_visit не изменился:', data.me.is_first_visit)
        }
      } else {
        console.log('⚠️ is_first_visit не найден в ответе API')
      }
    }

    // NOTE: cases больше НЕ устанавливаем здесь — это вызывало частичное
    // обновление кейсов из других API-ответов (например /api/me/ без приватных
    // кейсов), из-за чего приватные кейсы «дозагружались» на странице /cases.
    // Список кейсов управляется только через loadCases() в useCasesStore,
    // где cases и categoryOrder устанавливаются вместе за один set.

    // Handle case details
    if (data.case) {
      const { setCurrentCase } = useCasesStore.getState()
      setCurrentCase(data.case)
    }

    // Handle inventory data
    if (data.inventory) {
      const { setItems } = useInventoryStore.getState()
      setItems(data.inventory)
    }

    // Handle case opening result
    if (data.opened_item) {
      const { setOpeningResult } = useCasesStore.getState()
      setOpeningResult(data.opened_item)
    }

    // Handle alerts (success messages)
    if (data.alert) {
      toast.success(data.alert)
    }

    // Handle notifications
    if (data.notification) {
      toast(data.notification, {
        icon: '🔔',
        style: {
          background: 'linear-gradient(135deg, #4EFFC6 0%, #00FF7F 100%)',
          color: '#0A0A0F',
        }
      })
    }

    // Handle balance updates (fallback для старых API)
    if (!skipBalanceSync && data.balance !== undefined && !data.me) {
      const { setBalance } = useAppStore.getState()
      const normalizedBalance = Number(data.balance)
      setBalance(Number.isFinite(normalizedBalance) ? normalizedBalance : 0)
    }

    // Handle tasks updates (fallback для старых API)
    if (data.tasks && !data.me) {
      const { setTasks } = useAppStore.getState()
      setTasks(data.tasks)
    }

    // Handle recent wins
    if (data.recent_wins) {
      const { setRecentWins } = useAppStore.getState()
      setRecentWins(data.recent_wins)
    }
  }

  /**
   * GET request wrapper
   */
  async get(endpoint, params = {}) {
    const url = new URL(this.getApiUrl(endpoint))
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key])
      }
    })

    return this.makeRequest(url.pathname + url.search)
  }

  /**
   * POST request wrapper
   */
  async post(endpoint, body = {}) {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  /**
   * POST request wrapper для form-urlencoded данных
   */
  async postForm(endpoint, body = {}) {
    const formData = new URLSearchParams()
    Object.keys(body).forEach(key => {
      if (body[key] !== undefined && body[key] !== null) {
        formData.append(key, body[key])
      }
    })

    return this.makeRequest(endpoint, {
      method: 'POST',
      body: formData.toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  /**
   * PUT request wrapper
   */
  async put(endpoint, body = {}) {
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body)
    })
  }

  /**
   * DELETE request wrapper
   */
  async delete(endpoint) {
    return this.makeRequest(endpoint, {
      method: 'DELETE'
    })
  }
}

// Create singleton instance
const apiService = new ApiService()

export default apiService
